import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import Image from './Image';
import LoadingIndicator from './LoadingIndicator';

export const VERB_BUTTON_TYPE = {
    PRIMARY_DEFAULT: 'primary-default',
    PRIMARY_DETAILS: 'primary-details',
    SECONDARY_DEFAULT: 'secondary-default',
    SECONDARY_DETAILS: 'secondary-details',
    SECONDARY_DETAILS_SELECTED: 'secondary-details-selected',
    CRITICAL: 'critical'
};

export const FONT_AWESOME_ICON_WEIGHT = {
    SOLID: 'fas',
    REGULAR: 'far',
    LIGHT: 'fal',
    BRAND: 'fab'
};

class VerbButton extends PureComponent {
    static propTypes = {
        textId: PropTypes.string,
        isLoading: PropTypes.bool,
        description: PropTypes.string,
        defaultMessage: PropTypes.string,
        type: PropTypes.string,
        buttonType: PropTypes.oneOf([
            VERB_BUTTON_TYPE.PRIMARY_DEFAULT,
            VERB_BUTTON_TYPE.PRIMARY_DETAILS,
            VERB_BUTTON_TYPE.SECONDARY_DEFAULT,
            VERB_BUTTON_TYPE.SECONDARY_DETAILS,
            VERB_BUTTON_TYPE.SECONDARY_DETAILS_SELECTED,
            VERB_BUTTON_TYPE.CRITICAL
        ]),
        onClick: PropTypes.func,
        className: PropTypes.string,
        fontAwesomeIcon: PropTypes.string,
        trailingFontAwesomeIcon: PropTypes.string,
        fontAwesomeIconWeight: PropTypes.oneOf([
            FONT_AWESOME_ICON_WEIGHT.SOLID,
            FONT_AWESOME_ICON_WEIGHT.REGULAR,
            FONT_AWESOME_ICON_WEIGHT.LIGHT,
            FONT_AWESOME_ICON_WEIGHT.BRAND
        ]),
        isDarkBackground: PropTypes.bool,
        buttonText: PropTypes.string,
        children: PropTypes.string,
        isSelected: PropTypes.bool,
        disabled: PropTypes.bool,
        newTab: PropTypes.bool,
        upperCaseText: PropTypes.bool,
        href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        imageUrl: PropTypes.string,
        imageDimensions: PropTypes.object,
        external: PropTypes.bool,
        linkLocationState: PropTypes.object,
        heading6BtnText: PropTypes.bool,
    };

    getFontAwesomeIcon = (isTrailingIcon = false) => {
        const {
            fontAwesomeIcon,
            trailingFontAwesomeIcon,
            fontAwesomeIconWeight = FONT_AWESOME_ICON_WEIGHT.LIGHT,
            buttonType = VERB_BUTTON_TYPE.PRIMARY_DEFAULT
        } = this.props;
        if (
            (!fontAwesomeIcon && !trailingFontAwesomeIcon) ||
            (!fontAwesomeIcon && !isTrailingIcon) ||
            (!trailingFontAwesomeIcon && isTrailingIcon)
        ) {
            return '';
        }
        const fixedWidth = buttonType !== VERB_BUTTON_TYPE.SECONDARY_DETAILS ? 'fa-fw' : '';
        return cns(
            'icon',
            fontAwesomeIconWeight,
            fixedWidth,
            isTrailingIcon ? trailingFontAwesomeIcon : fontAwesomeIcon
        );
    };

    getButtonClassName = () => {
        const {
            buttonType = VERB_BUTTON_TYPE.PRIMARY_DEFAULT,
            className = '',
            isDarkBackground = false,
            isSelected,
            disabled
        } = this.props;
        const selectClass = isSelected ? 'selected' : '';
        return cns('verbStandardButton', { dark: isDarkBackground, disabled }, buttonType, className, selectClass);
    };

    render() {
        const {
            onClick,
            buttonText,
            type = 'button',
            children,
            disabled,
            href,
            textId,
            defaultMessage,
            trailingFontAwesomeIcon,
            description,
            newTab = false,
            upperCaseText = true,
            imageUrl,
            imageDimensions,
            external = false,
            linkLocationState = {},
            heading6BtnText = false,
            isLoading= false,
        } = this.props;

        let text;
        const target = newTab ? { target: '_blank' } : {};
        const buttonProps = {
            onClick: !disabled ? onClick : () => {},
            className: this.getButtonClassName(),
            disabled,
            type
        };

        if (textId && !isLoading) {
            text = (
                <FormattedMessage
                    id={textId}
                    defaultMessage={upperCaseText ? defaultMessage.toLocaleUpperCase() : defaultMessage}
                    description={description}
                />
            );
        } else if (buttonText) {
            text = upperCaseText ? buttonText.toLocaleUpperCase() : buttonText;
        } else if (isLoading) {
            text = <i className={'spinner fa fa-spinner fa-spin'} />;
        }
        else {
            text = children;
        }

        if (href) {
            const linkInternals = (
                <button {...buttonProps}>
                    {imageUrl ? (
                        <span className={'icon'}>
                            <Image src={imageUrl} dimension={imageDimensions} alt={''} className={''} />
                        </span>
                    ) : null}
                    <span className={this.getFontAwesomeIcon()} />
                    {text}
                    <span
                        className={cns({ 'trailing-icon': trailingFontAwesomeIcon }, this.getFontAwesomeIcon(true))}
                    />
                </button>
            );

            if (external) {
                return (
                    <a href={href} {...target}>
                        {linkInternals}
                    </a>
                );
            }
            return (
                <Link
                    to={isEmpty(linkLocationState) ? href : { pathname: href, state: { ...linkLocationState } }}
                    {...target}
                >
                    {linkInternals}
                </Link>
            );
        }

        return (
            <button {...buttonProps}>
                {imageUrl ? (
                    <span className={'icon'}>
                        <Image src={imageUrl} dimension={imageDimensions} alt={''} className={''} />
                    </span>
                ) : null}
                <span className={this.getFontAwesomeIcon()} />
                { heading6BtnText ? <h6>{text}</h6> : <div>{text}</div> }
                <span className={cns({ 'trailing-icon': trailingFontAwesomeIcon }, this.getFontAwesomeIcon(true))} />
            </button>
        );
    }
}

export default VerbButton;
